// src/ui/components/dialogs/FullscreenDialog.components.tsx
import React from 'react';
import { SpecItem } from '../../../core/models/Block';

/**
 * Estilos CSS inline para el contenido del diálogo
 */
export const DialogContentStyles: React.FC = () => (
  <style
    dangerouslySetInnerHTML={{
      __html: `
        .fullscreen-dialog-content * { 
          font-size: inherit !important; 
          line-height: inherit !important; 
        }
        .fullscreen-dialog-content ul, 
        .fullscreen-dialog-content ol {
          list-style: none !important;
          list-style-type: none !important;
          padding-left: 0 !important;
          margin-left: 0 !important;
        }
        .fullscreen-dialog-content li {
          list-style: none !important;
          list-style-type: none !important;
          padding-left: 0 !important;
          margin: 0 0 0.5em 0 !important;
        }
        .fullscreen-dialog-content li::before {
          content: none !important;
          display: none !important;
        }
        .fullscreen-dialog-content li:last-child {
          margin-bottom: 0 !important;
        }
      `,
    }}
  />
);

/**
 * Componente de carga para videos
 */
export const LoadingSpinner: React.FC<{ message?: string }> = ({ message = 'Cargando video...' }) => (
  <div className="flex items-center justify-center h-[45vh] bg-gray-900 rounded">
    <div className="text-white text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
      <p className="text-sm">{message}</p>
    </div>
  </div>
);

/**
 * Badge individual para mostrar una spec
 */
interface SpecBadgeProps {
  spec: SpecItem;
  variant?: 'default' | 'dark';
}

export const SpecBadge: React.FC<SpecBadgeProps> = ({ spec, variant = 'default' }) => {
  const bgClass = variant === 'dark' ? 'bg-black/50' : 'bg-white/20';
  const borderClass = variant === 'dark' ? 'border-white/40' : 'border-white/30';
  const textClass = variant === 'dark' ? 'text-white/90' : 'text-white/80';

  return (
    <div
      className={`flex items-center gap-2 ${bgClass} backdrop-blur-md rounded-lg ${borderClass} shadow-md px-3 py-1.5`}
    >
      <span
        className={`${textClass} font-semibold uppercase tracking-wide text-xs`}
        style={{ fontSize: 'clamp(0.75rem, 1.2vw, 1.5rem)' }}
      >
        {spec.title}
      </span>
      <span
        className="text-white font-bold"
        style={{ fontSize: 'clamp(1rem, 1.8vw, 2.5rem)' }}
      >
        {spec.value}
      </span>
    </div>
  );
};

/**
 * Contenedor de specs (badges)
 */
interface SpecsContainerProps {
  specs: SpecItem[];
  variant?: 'default' | 'dark';
  className?: string;
}

export const SpecsContainer: React.FC<SpecsContainerProps> = ({
  specs,
  variant = 'default',
  className = '',
}) => {
  if (!specs || specs.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-2 justify-center ${className}`}>
      {specs.map((spec, idx) => (
        <SpecBadge key={idx} spec={spec} variant={variant} />
      ))}
    </div>
  );
};

/**
 * Header con título y specs
 */
interface DialogHeaderProps {
  title: string;
  blockSpecs?: SpecItem[][];
  titleStyle?: 'large' | 'medium';
  variant?: 'default' | 'dark';
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  blockSpecs,
  titleStyle = 'large',
  variant = 'default',
}) => {
  const fontSize = titleStyle === 'large' ? 'clamp(2rem, 4.5vw, 4rem)' : 'clamp(1.8rem, 4vw, 3.5rem)';
  const textShadow = variant === 'dark' ? '2px 2px 8px rgba(0,0,0,0.9)' : '2px 2px 8px rgba(0,0,0,0.7)';
  const specs = blockSpecs?.flat() || [];

  return (
    <div className="absolute top-6 left-0 right-0 z-10 px-8 flex items-start justify-between gap-6 pb-4">
      <div className="flex-shrink-0">
        <h1
          className="text-white font-bold"
          style={{
            fontSize,
            textShadow,
            fontWeight: 700,
            lineHeight: '1.2',
          }}
        >
          {title}
        </h1>
      </div>
      {specs.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 justify-end flex-shrink">
          {specs.map((spec, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-3 ${variant === 'dark' ? 'bg-black/50' : 'bg-white/20'
                } backdrop-blur-md rounded-xl ${variant === 'dark' ? 'border-white/40' : 'border-white/30'
                } shadow-lg px-4 py-2`}
            >
              <span
                className={`${variant === 'dark' ? 'text-white/90' : 'text-white/80'
                  } font-semibold uppercase tracking-wide`}
                style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1.8rem)' }}
              >
                {spec.title}
              </span>
              <span
                className="text-white font-bold"
                style={{ fontSize: 'clamp(1.25rem, 2.2vw, 3.5rem)' }}
              >
                {spec.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/**
 * Header simple solo con título (para layout de video)
 */
interface SimpleHeaderProps {
  title: string;
}

export const SimpleHeader: React.FC<SimpleHeaderProps> = ({ title }) => (
  <div className="absolute top-4 left-0 right-0 z-10 px-6">
    <h1
      className="text-white font-bold"
      style={{
        fontSize: 'clamp(1.8rem, 4vw, 3.5rem)',
        textShadow: '2px 2px 6px rgba(0,0,0,0.7)',
        fontWeight: 700,
        lineHeight: '1.2',
      }}
    >
      {title}
    </h1>
  </div>
);





